import { ApolloProvider } from "@apollo/client/react";
import React, { Suspense, useContext, useReducer, useMemo } from "react";
import ReactDOM from "react-dom";
import TagManager from "react-gtm-module";
import { Provider } from "react-redux";
import Clinet from "./Client";
import * as Sentry from "@sentry/react";

import { configureStore } from "./redux/store";
import Context from "./store/Context";
import Reducer from "./store/Reducer";
import { WebSocketProvider } from "./store/WebSocketContext";

const infathGtmArgs = {
  gtmId: process.env.REACT_APP_INFATH_GTM,
};

const mobasherGtmArgs = {
  gtmId: process.env.REACT_APP_MOBASHER_GTM,
  dataLayerName: "dataLayer2",
};

Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_DSN,
  sendDefaultPii: true,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 0.1,
});

const reduxStore = configureStore();

function runWhenIdle(fn) {
  if ("requestIdleCallback" in window) {
    requestIdleCallback(() => fn(), { timeout: 2000 });
  } else {
    setTimeout(fn, 1200);
  }
}

const GTM_INIT_KEY = "__gtm_initialized__";

const initTracking = () => {
  if (window[GTM_INIT_KEY]) return;
  window[GTM_INIT_KEY] = true;

  runWhenIdle(() => TagManager.initialize(infathGtmArgs));
  setTimeout(
    () => runWhenIdle(() => TagManager.initialize(mobasherGtmArgs)),
    500,
  );
};

if (document.readyState === "complete") {
  initTracking();
} else {
  window.addEventListener("load", initTracking);
}

const App = React.lazy(() => import(/* webpackChunkName: "App" */ "./App"));

const Store = (props) => {
  const initialState = useContext(Context);
  const [state, dispatch] = useReducer(Reducer, initialState);

  const contextValue = useMemo(() => ({ state, dispatch }), [state]);

  return (
    <Context.Provider value={contextValue}>{props.children}</Context.Provider>
  );
};

ReactDOM.render(
  <Sentry.ErrorBoundary>
    <Store>
      <ApolloProvider client={Clinet}>
        <Provider store={reduxStore}>
          <Suspense fallback={<div className="loading" />}>
            <WebSocketProvider>
              <App />
            </WebSocketProvider>
          </Suspense>
        </Provider>
      </ApolloProvider>
    </Store>
  </Sentry.ErrorBoundary>,
  document.getElementById("root"),
);
